import { EOperator } from "@interfaces/operator.enum";
import { IProxy } from "@interfaces/user/proxy";
import { ITariff } from "@interfaces/user/tariff";
import { authApi } from "@RTKQuery/basic";
import authAuthApi from "../auth/auth";
import { ITag } from "@interfaces/user/tag";

const purchasesAuthApi = authApi.injectEndpoints({
    endpoints: (build) => ({
        buyProxy: build.mutation<boolean, {
            tariff_name: ITariff["name"],
            modems_quantity: number,
            days: number,
            operator: EOperator,
        }>({
            query: (body) => {
                return {
                    url: "purchases/bye_proxies",
                    method: "POST",
                    body
                }
            },
            invalidatesTags: ["ProxyUser"]
        }),
        getAllUserProxies: build.query<{
            count_items: number,
            purchases: IProxy[]
        }, {
            page: number,
            limit: number,
            search: string,
            tag_ids: ITag["id"][]
        }>({
            query: ({ page, limit, search, tag_ids }) => ({
                url: "purchases" + (tag_ids.length ? "?" + tag_ids.map(tag => "tag_ids=" + tag).join("&") : ""),
                params: Object.assign(
                    {
                        page: page,
                        size: limit
                    },
                    search ? { search } : {},
                )
            }),
            providesTags: ["ProxyUser"]
        }),
        getOneFreeProxy: build.mutation<IProxy[], { operator: EOperator }>({
            query: ({ operator }) => {
                return {
                    url: "purchases/get_test_proxy",
                    method: "GET",
                    params: { operator }
                }
            },
            invalidatesTags: ["ProxyUser"]
        }),
        updateAutoContinueStatus: build.mutation<IProxy, {
            purchase_id: IProxy["id"],
            params: {
                page: number,
                limit: number,
                search: string,
                tag_ids: ITag["id"][]
            },
            auto_continue: IProxy["auto_continue"]
        }>({
            query: ({ purchase_id, auto_continue }) => ({
                url: "purchases/" + purchase_id,
                method: "PUT",
                body: {
                    auto_continue
                }
            }),
            async onQueryStarted({ purchase_id, params }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        purchasesAuthApi.util.updateQueryData('getAllUserProxies', params, (draft) => {
                            draft.purchases = draft.purchases.map(item => {
                                if (item.id === purchase_id) {
                                    return data;
                                } else {
                                    return item;
                                }
                            })
                        }),
                    )
                } catch { }
            },
        }),
        updateDaysForContinue: build.mutation<IProxy, {
            purchase_id: IProxy["id"],
            params: {
                page: number,
                limit: number,
                search: string,
                tag_ids: ITag["id"][]
            },
            days_for_continue: IProxy["days_for_continue"]
        }>({
            query: ({ purchase_id, days_for_continue }) => ({
                url: "purchases/" + purchase_id + "/days_for_continue",
                method: "PUT",
                body: {
                    days_for_continue
                }
            }),
            async onQueryStarted({ purchase_id, params }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        purchasesAuthApi.util.updateQueryData('getAllUserProxies', params, (draft) => {
                            draft.purchases = draft.purchases.map(item => {
                                if (item.id === purchase_id) {
                                    return data;
                                } else {
                                    return item;
                                }
                            })
                        }),
                    )
                } catch { }
            }
        }),
        changeOperatorPurchase: build.mutation<IProxy, {
            purchase_id: IProxy["id"],
            params: {
                page: number,
                limit: number,
                search: string,
                tag_ids: ITag["id"][]
            },
            body: {
                tariff_name: ITariff["name"],
                operator: EOperator,
                proxy_router_id: IProxy["router_id"]
            }
        }>({
            query: ({ purchase_id, body }) => ({
                url: `purchases/${purchase_id}/change_operator`,
                method: "PUT",
                body
            }),
            async onQueryStarted({ purchase_id, params }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(
                        purchasesAuthApi.util.updateQueryData('getAllUserProxies', params, (draft) => {
                            draft.purchases = draft.purchases.map(item => {
                                if (item.id === purchase_id) {
                                    return data;
                                } else {
                                    return item;
                                }
                            })
                        }),
                    )
                } catch { }
            },
        }),
        getFreeOperatorsPurchase: build.query<{
            names: EOperator[],
            tariff: ITariff["name"],
            proxy_router_id: IProxy["router_id"]
        }, { purchase_id: IProxy["id"] }>({
            query: ({ purchase_id }) => `purchases/${purchase_id}/free_operators`
        }),
        updateTimeChangeIpAddress: build.mutation<void, {
            purchase_id: IProxy["id"],
            time: number,
            params: {
                page: number,
                limit: number,
                search: string,
                tag_ids: ITag["id"][]
            },
        }>({
            query: ({ purchase_id, time }) => ({
                url: `purchases/${purchase_id}/time_change_ip`,
                method: "PUT",
                body: { time }
            }),
            async onQueryStarted({ purchase_id, time, params }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(
                        purchasesAuthApi.util.updateQueryData('getAllUserProxies', params, (draft) => {
                            draft.purchases = draft.purchases.map(item => {
                                if (item.id === purchase_id) {
                                    return {
                                        ...item,
                                        change_ip_time: time
                                    };
                                } else {
                                    return item;
                                }
                            })
                        }),
                    )
                } catch { }
            },
        }),
        updatePasswordProxy: build.mutation<void, {
            purchase_id: IProxy["id"],
            password: string,
            params: {
                page: number,
                limit: number,
                search: string,
                tag_ids: ITag["id"][]
            },
        }>({
            query: ({ purchase_id, password }) => ({
                url: `purchases/${purchase_id}/change_api_password`,
                method: "PUT",
                body: { password }
            }),
            async onQueryStarted({ purchase_id, password, params }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(
                        purchasesAuthApi.util.updateQueryData('getAllUserProxies', params, (draft) => {
                            draft.purchases = draft.purchases.map(item => {
                                if (item.id === purchase_id) {
                                    return {
                                        ...item,
                                        proxy_password: password
                                    };
                                } else {
                                    return item;
                                }
                            })
                        }),
                    )
                } catch { }
            },
        }),
        extensionValidityPeriod: build.mutation<void, {
            purchase_id: IProxy["id"],
            term: number,
            numberPackages: number,
            totalPrice: number,
             params: {
                page: number,
                limit: number,
                search: string,
                tag_ids: ITag["id"][]
            },
        }>({
            query: ({ purchase_id, term, numberPackages }) => ({
                url: `purchases/${purchase_id}/continue`,
                method: "POST",
                body: { days: term, quantity: numberPackages }
            }),
            async onQueryStarted({ purchase_id, numberPackages, term, params, totalPrice }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(
                        purchasesAuthApi.util.updateQueryData('getAllUserProxies', params, (draft) => {
                            draft.purchases = draft.purchases.map(item => {
                                if (item.id === purchase_id) {
                                    const date = new Date(item.end_date || new Date())
                                    date.setUTCDate(date.getUTCDate() + (term * numberPackages))

                                    return {
                                        ...item,
                                        end_date: date.toISOString()
                                    };
                                } else {
                                    return item;
                                }
                            })
                        }),
                    )
                    dispatch(
                        authAuthApi.util.updateQueryData('getUser', undefined, (draft) => {
                            draft.balance -= totalPrice
                        }),
                    )
                } catch { }
            }
        })
    }),
    overrideExisting: false,
})

export const {
    useBuyProxyMutation,
    useGetAllUserProxiesQuery,
    useGetOneFreeProxyMutation,
    useUpdateAutoContinueStatusMutation,
    useUpdateDaysForContinueMutation,
    useChangeOperatorPurchaseMutation,
    useGetFreeOperatorsPurchaseQuery,
    useUpdateTimeChangeIpAddressMutation,
    useUpdatePasswordProxyMutation,
    useExtensionValidityPeriodMutation
} = purchasesAuthApi;

export default purchasesAuthApi;